<template>
  <div class="merge-vendors-container merge-vendors-main merge-vendors-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Merge Vendors']" />

    <form class="merge-vendors-form" @submit.prevent="handleMerge">
      <div class="glass-tab-view glass-tab-view--flush merge-vendors-glass-shell">
        <div class="glass-tab-view__content merge-vendors-page-stack">
          <div class="merge-vendors-header-block">
            <h2 class="merge-vendors-title">Merge Vendors</h2>
            <p class="merge-vendors-subtitle">
              Combine duplicate vendor records by moving activity from one vendor into another
            </p>
          </div>

          <div class="merge-vendors-comparison">
            <div class="merge-vendors-section merge-vendors-section--from glass-tab-panel--nested">
              <h3 class="merge-vendors-section-title">Vendor From</h3>
              <p class="merge-vendors-section-hint">Source record to merge away</p>
              <div class="merge-vendors-form-row merge-vendors-form-row--stack">
                <div class="merge-vendors-form-group merge-vendors-form-group--dropdown">
                  <LabelDropdown
                    label="Vendor"
                    select-id="merge-vendors-from"
                    v-model="selectedFrom"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search vendors..."
                    :options="vendorSelectOptions"
                  />
                </div>
                <div class="merge-vendors-form-group">
                  <LabelInputField
                    input-id="merge-vendors-from-id"
                    label="Vendor ID"
                    :model-value="fromVendor?.id || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-vendors-form-group merge-vendors-form-group--checkbox">
                  <LabelCheckboxField
                    input-id="merge-vendors-from-active"
                    label="Active"
                    :model-value="!!fromVendor?.active"
                    disabled
                    flex
                  />
                </div>
                <div
                  class="merge-vendors-form-group merge-vendors-form-group--balance"
                  :class="{ 'merge-vendors-form-group--balance-empty': !fromVendor?.balance }"
                >
                  <LabelInputField
                    input-id="merge-vendors-from-balance"
                    label="Balance"
                    :model-value="fromVendor?.balance || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>

            <div class="merge-vendors-comparison-arrow" aria-hidden="true">
              <i class="bi bi-arrow-right"></i>
            </div>

            <div class="merge-vendors-section merge-vendors-section--to glass-tab-panel--nested">
              <h3 class="merge-vendors-section-title">Vendor To</h3>
              <p class="merge-vendors-section-hint">Target record to keep</p>
              <div class="merge-vendors-form-row merge-vendors-form-row--stack">
                <div class="merge-vendors-form-group merge-vendors-form-group--dropdown">
                  <LabelDropdown
                    label="Vendor"
                    select-id="merge-vendors-to"
                    v-model="selectedTo"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search vendors..."
                    :options="vendorSelectOptions"
                  />
                </div>
                <div class="merge-vendors-form-group">
                  <LabelInputField
                    input-id="merge-vendors-to-id"
                    label="Vendor ID"
                    :model-value="toVendor?.id || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-vendors-form-group merge-vendors-form-group--checkbox">
                  <LabelCheckboxField
                    input-id="merge-vendors-to-active"
                    label="Active"
                    :model-value="!!toVendor?.active"
                    disabled
                    flex
                  />
                </div>
                <div
                  class="merge-vendors-form-group merge-vendors-form-group--balance"
                  :class="{ 'merge-vendors-form-group--balance-empty': !toVendor?.balance }"
                >
                  <LabelInputField
                    input-id="merge-vendors-to-balance"
                    label="Balance"
                    :model-value="toVendor?.balance || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="merge-vendors-form-actions glass-tab-view__actions">
            <SecondaryButton label="Cancel" @click="handleCancel" />
            <PrimaryButton
              label="Merge"
              icon="bi bi-arrow-left-right"
              @click="handleMerge"
            />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import '../styles/MergeVendors.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'MergeVendors',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    LabelInputField,
    LabelCheckboxField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      vendors: [
        { id: 'V-001', name: 'Alpha Supplies', active: true, balance: '$3,200.00' },
        { id: 'V-002', name: 'Bravo Traders', active: false, balance: '$0.00' },
        { id: 'V-003', name: 'Charlie Goods', active: true, balance: '$1,150.00' },
        { id: 'V-004', name: 'Delta Wholesale', active: true, balance: '$500.00' },
      ],
      selectedFrom: '',
      selectedTo: '',
    };
  },
  computed: {
    vendorSelectOptions() {
      return this.vendors.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
      }));
    },
    fromVendor() {
      return this.vendors.find((v) => v.id === this.selectedFrom);
    },
    toVendor() {
      return this.vendors.find((v) => v.id === this.selectedTo);
    },
  },
  methods: {
    handleMerge() {
      if (!this.selectedFrom || !this.selectedTo) {
        this.$toast && this.$toast.error('Please select both Vendor From and Vendor To.');
        return;
      }
      if (this.selectedFrom === this.selectedTo) {
        this.$toast && this.$toast.error('Vendor From and Vendor To must be different.');
        return;
      }
      this.$toast && this.$toast.info('Merge vendors coming soon!');
    },
    handleCancel() {
      if (this.$router) {
        this.$router.go(-1);
        return;
      }
      this.selectedFrom = '';
      this.selectedTo = '';
    },
  },
};
</script>
